/*
 * Helpers for the pail file browser.
 *
 * pail serves it under `/v1/cids/:cid/filebrowser/:credential/buckets[/:bucket/objects]`.
 * The credential and bucket are path segments and the object key travels as a `key` or
 * `prefix` query param - no `s3://` URI is ever sent. The UI still keeps one, because a
 * bucket URI is what a user pastes into a job config and what Map My Data / Graph My
 * Data carry around, so paths are held as URIs and split at the call site.
 *
 * The same endpoints serve AWS and GCS; pail infers the cloud from the credential, so
 * the scheme matters only for what the UI displays.
 */

// aquacred coordinates of the credentials pail accepts for the file browser. These live
// under the `filebrowser` service; pail resolves `dev` as `<cid>.dev`.
const FILE_BROWSER_CRED_SERVICE = "filebrowser";
const FILE_BROWSER_CRED_NAMESPACE = "default";

// pail falls back to this credential when the path segment is empty
const DEFAULT_FILE_BROWSER_CREDENTIAL = "default";

// Only credential types that point at an object store can browse buckets.
const SCHEME_BY_CREDENTIAL_TYPE = {
    aws: "s3",
    gcp: "gs",
};

const BROWSABLE_CREDENTIAL_TYPES = Object.keys(SCHEME_BY_CREDENTIAL_TYPE);

// Short name for the store behind a credential, shown next to the credential name so it
// is obvious which cloud a selection reaches before any request is made.
const CLOUD_LABEL_BY_CREDENTIAL_TYPE = {
    aws: "S3",
    gcp: "GCS",
};

// Service-side bounds, mirrored so the UI fails before the request does.
// See pail server/internal/service/file_browser_v2.go.
const MAX_LIST_RESULTS = 10000;            // objects?max_results
const MAX_PARSER_LIMIT = 500;              // objects/preview?limit and content_parser limit

// Parsing pulls the whole object through the service, so anything larger is rejected in
// the UI rather than sent and timed out.
const MAX_PARSABLE_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

// The only types either parser endpoint accepts.
const PARSABLE_FILE_TYPES = ["avro", "parquet"];

function schemeForCredentialType(type) {
    return SCHEME_BY_CREDENTIAL_TYPE[type] || null;
}

function cloudLabelForCredentialType(type) {
    return CLOUD_LABEL_BY_CREDENTIAL_TYPE[type] || null;
}

function isBrowsableCredential(credential) {
    return !!credential && BROWSABLE_CREDENTIAL_TYPES.includes(credential.type);
}

/*
 * Base URL for one credential's browser endpoints. `bucket` is optional so the same
 * builder serves the bucket list and everything under a bucket.
 */
function fileBrowserUrl(pailUrl, cid, credential, bucket) {
    const base = `${pailUrl}/v1/cids/${encodeURIComponent(cid)}`
        + `/filebrowser/${encodeURIComponent(credential || DEFAULT_FILE_BROWSER_CREDENTIAL)}/buckets`;
    return bucket ? `${base}/${encodeURIComponent(bucket)}/objects` : base;
}

// "s3://bucket/a/b/c.txt" -> { scheme: "s3", bucket: "bucket", key: "a/b/c.txt" }
function parseStoragePath(path) {
    if (!path) return null;
    const match = /^([a-z0-9]+):\/\/([^/]+)\/?(.*)$/i.exec(path);
    if (!match) return null;
    return { scheme: match[1], bucket: match[2], key: match[3] || "" };
}

function buildStoragePath(scheme, bucket, key = "") {
    if (!scheme || !bucket) return "";
    return `${scheme}://${bucket}/${key}`;
}

function fileExtension(name) {
    if (!name) return "";
    const parts = name.split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

function isParsableFile(name) {
    return PARSABLE_FILE_TYPES.includes(fileExtension(name));
}

// pail rejects anything outside avro/parquet, so a guessed type is only worth sending
// when it is one of those - otherwise the service infers it from the key.
function parsableFileType(type) {
    return PARSABLE_FILE_TYPES.includes(type) ? type : undefined;
}

/*
 * aqfs names every listed entry relative to the prefix that was requested (it slices at
 * the prefix's last "/"), so a name is appended to the current path as-is. The guard
 * against a name that still carries the prefix costs nothing and keeps navigation right
 * if that ever changes.
 */
function relativeObjectName(name, prefixKey) {
    if (!name) return "";
    if (prefixKey && name !== prefixKey && name.startsWith(prefixKey)) {
        return name.slice(prefixKey.length);
    }
    return name;
}

// Display/route identifier for a listed entry: its name relative to the folder in view,
// without the trailing slash directories carry. An object keyed literally "/" reduces to
// "" - Datatable substitutes its own "slash" sentinel for that case.
function objectDisplayName(name, prefixKey) {
    const relative = relativeObjectName(name, prefixKey);
    return relative.endsWith("/") ? relative.slice(0, -1) : relative;
}

/*
 * pail returns `{name, isDir, sizeBytes, lastModified}` (see pail types.FileBrowserObject)
 * and omits sizeBytes/lastModified where there is none. Earlier builds used snake_case,
 * so both are read here - a version skew between environments should not blank a listing.
 */
function normalizeObjects(items, prefixKey) {
    return (items || []).map((item) => ({
        name: item.name,
        is_dir: !!(item.isDir ?? item.is_dir),
        size: item.sizeBytes ?? item.size ?? 0,
        time: item.lastModified ?? item.time ?? "",
        folderName: objectDisplayName(item.name, prefixKey),
    }));
}

// pail returns `{name, createdAt}` per bucket; earlier builds used creation_date.
function normalizeBuckets(buckets) {
    return (buckets || []).map((bucket) => ({
        name: bucket.name,
        createdAt: bucket.createdAt ?? bucket.creation_date ?? "",
    }));
}

/*
 * Listing glyphs. A folder listing is scanned, not read, so each row leads with an icon
 * whose shape and tone say what the object is before the name is parsed. Tones are
 * file-browser token classes (see fileBrowser.css) so they follow the theme into dark
 * mode without each row knowing which theme is active.
 */
const FOLDER_GLYPH = { icon: "fa-folder", tone: "fb-glyph-folder" };
const DEFAULT_GLYPH = { icon: "fa-file", tone: "fb-glyph-muted" };
const ARCHIVE_GLYPH = { icon: "fa-file-zipper", tone: "fb-glyph-muted" };

const FILE_GLYPHS = [
    // avro/parquet are the formats this product can preview, so they lead the list and
    // carry the accent tone - the glyph stays file-shaped like every other row
    { extensions: ["parquet", "avro", "orc"], glyph: { icon: "fa-file-lines", tone: "fb-glyph-data" } },
    { extensions: ["csv", "tsv", "psv"], glyph: { icon: "fa-file-csv", tone: "fb-glyph-table" } },
    { extensions: ["json", "yaml", "yml", "xml", "sql", "py", "go", "js", "sh"], glyph: { icon: "fa-file-code", tone: "fb-glyph-code" } },
    { extensions: ["png", "jpg", "jpeg", "gif", "svg", "webp"], glyph: { icon: "fa-file-image", tone: "fb-glyph-muted" } },
    // plain text keeps the neutral file so it never reads as a data format
    { extensions: ["txt", "log", "md"], glyph: { icon: "fa-file", tone: "fb-glyph-muted" } },
];

// Stripped before classifying so `part-0.avro.gz` still reads as an avro file
const COMPRESSION_EXTENSIONS = ["gz", "bz2", "zst", "snappy", "zip", "tar"];

function fileGlyph(name, isDir) {
    if (isDir) return FOLDER_GLYPH;
    let extension = fileExtension(name);
    let compressed = false;
    if (COMPRESSION_EXTENSIONS.includes(extension)) {
        compressed = true;
        extension = fileExtension(name.slice(0, -(extension.length + 1)));
    }
    const match = FILE_GLYPHS.find((entry) => entry.extensions.includes(extension));
    if (match) return match.glyph;
    return compressed ? ARCHIVE_GLYPH : DEFAULT_GLYPH;
}

export {
    FILE_BROWSER_CRED_SERVICE,
    FILE_BROWSER_CRED_NAMESPACE,
    MAX_LIST_RESULTS,
    MAX_PARSER_LIMIT,
    MAX_PARSABLE_FILE_SIZE,
    fileBrowserUrl,
    schemeForCredentialType,
    cloudLabelForCredentialType,
    fileGlyph,
    isBrowsableCredential,
    parseStoragePath,
    buildStoragePath,
    fileExtension,
    isParsableFile,
    parsableFileType,
    objectDisplayName,
    normalizeObjects,
    normalizeBuckets,
};
